import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { firstValueFrom } from 'rxjs';
import emailjs, { type EmailJSResponseStatus } from '@emailjs/browser';
import { Title, Meta, DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { Business, BusinessService } from '../../../services/business.service';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-home-eighth',
  templateUrl: './home-eighth.component.html',
  styleUrls: ['./home-eighth.component.css']
})
export class HomeEighthComponent implements OnInit {
  business: Business | null = null;
  error: string | null = null;
  menuUrl: string = '';

  customOptions: OwlOptions = {
    loop: true,
    dots: true,
    margin: 0,
    nav: true,
    autoplay: true,
    autoplaySpeed: 2000,
    navSpeed: 2000,
    items: 1,
    navText: ['<i class="icofont icofont-arrow-left"></i>', '<i class="icofont icofont-arrow-right"></i>'],
  };

  formData = {
    user_name: '',
    user_email: '',
    message: ''
  };

  private serviceId = 'contact-form'; // Replace with your EmailJS Service ID
  private templateId = 'contact-form-becki'; // Replace with your EmailJS Template ID
  private publicKey = 'gObMl0a3V0Jib9k5c'; // Replace with your EmailJS Public Key

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private businessService: BusinessService,
    private titleService: Title,
    private metaService: Meta,
    private sanitizer: DomSanitizer
  ) { }

  async ngOnInit(): Promise<void> {
    const businessId = this.route.snapshot.paramMap.get('businessId');
    if (businessId && !isNaN(+businessId)) {
      try {
        this.business = await firstValueFrom(this.businessService.getBusiness(+businessId));
        this.menuUrl = `${environment.templateUrl}/?businessId=${businessId}`;
        if (this.business.favicon) {
          this.metaService.updateTag({ name: 'icon', content: `${environment.baseUrl}${this.business.favicon}` });
        }
        if (this.business.name) {
          this.titleService.setTitle(this.business.name);
        }
        // Update slider options based on number of sliders
        if (this.business.sliders && this.business.sliders.length === 1) {
          this.customOptions = { ...this.customOptions, loop: false, nav: false, dots: false };
        }
      } catch (err: any) {
        this.error = err.message || 'Failed to load business';
        this.router.navigate(['/not-found'], {
          queryParams: { businessId, error: 'not-found' }
        });
      }
    } else {
      this.router.navigate(['/home']);
    }
  }

  getLogoUrl(logoPath: string): string {
    return logoPath ? `${environment.baseUrl}${logoPath}` : '/assets/placeholder.png';
  }

  getSliderImageUrl(slider: { image_url?: string }): SafeStyle {
    const src = slider.image_url ? `${environment.baseUrl}${slider.image_url}` : '/assets/placeholder.png';
    return this.sanitizer.bypassSecurityTrustStyle(`url("${src}")`);
  }

  getServiceIconUrl(icon_url: string): string {
    return icon_url ? `${environment.baseUrl}${icon_url}` : '/assets/placeholder.png';
  }

  getServiceColumnClass(serviceCount: number): string {
    return serviceCount <= 2 ? 'col-md-6 col-sm-6 col-xs-12' : 'col-md-4 col-sm-4 col-xs-12';
  }

  public sendEmail(event: Event): void {
    event.preventDefault();
    emailjs.sendForm(this.serviceId, this.templateId, event.target as HTMLFormElement, this.publicKey)
      .then(
        (response: EmailJSResponseStatus) => {
          alert('Message sent successfully. Thank you, we will get back to you soon!');
          this.resetForm();
        },
        (error: EmailJSResponseStatus) => {
          console.error('Failed to send message. Error:', error.text);
          alert('Failed to send the message. Please try again later.');
        }
      );
  }

  private resetForm(): void {
    this.formData = {
      user_name: '',
      user_email: '',
      message: ''
    };
  }

  getSocialMediaLinks(): { platform: string; link: string; icon: string }[] {
    if (!this.business?.social_urls) return [];
    const socials = this.business.social_urls;
    const platforms: { platform: string; key: keyof typeof socials; icon: string }[] = [
      { platform: 'Facebook', key: 'facebook', icon: 'icofont icofont-social-facebook' },
      { platform: 'Instagram', key: 'instagram', icon: 'icofont icofont-social-instagram' },
      { platform: 'Behance', key: 'behance', icon: 'icofont icofont-social-behance' },
      { platform: 'LinkedIn', key: 'linkedin', icon: 'icofont icofont-social-linkedin' },
      { platform: 'TikTok', key: 'tiktok', icon: 'fab fa-tiktok' },
      { platform: 'YouTube', key: 'youtube', icon: 'icofont icofont-social-youtube' },
      { platform: 'X (Twitter)', key: 'twitter', icon: 'icofont icofont-social-twitter' },
      { platform: 'WhatsApp', key: 'whatsapp', icon: 'icofont icofont-social-whatsapp' },
      { platform: 'Google Reviews', key: 'google_reviews', icon: 'fas fa-star' }
    ];
    return platforms
      .filter(p => socials[p.key] && typeof socials[p.key] === 'string')
      .map(p => ({ platform: p.platform, link: socials[p.key] as string, icon: p.icon }));
  }
}